import React from 'react'
import { RadioButtonVariant } from '@zuto/atoms/src/RadioButton'
import { TextStyle } from '@zuto/types'
import $console from '@zuto/utilities/src/$console'
import $dataLayer from '@zuto/utilities/src/$dataLayer'
import { Action } from '../ApplicationForm/ApplicationFormContext'

type GACategory = 'Application Form' | 'Form Abandonment' | 'FormlessJourney' | 'isNullOrEmpty'

export type GAAction = 'Question Visible'
  | 'Question Edited'
  | 'Edit Section'
  | 'Start'
  | 'Form Progression'
  | 'Input'
  | 'Next Question'
  | 'Previous Question'
  | 'Validation Error'
  | 'Validation'
  | 'Loan Amount'
  | 'CTA Clicked'
  | 'Show Hint'
  | 'Submit'
  | 'Cannot Proceed'
  | 'Trustpilot Clicked'
  | 'Delete Address'
  | 'Edit Address'
  | 'Delete Employment'
  | 'Edit Employment'
  | 'Incomplete Address Entries'
  | 'Incomplete Employment Entries'
  | 'Accordion Open'
  | 'Accordion Closed'
  | 'Postcode Lookup'
  | 'Vehicle Lookup'
  | 'Form Abandonment'
  | 'Marketing Preferences'
  | 'Interstitial Started'
  | 'Interstitial Ended'
  | 'FormlessInterstitial Loaded'
  | 'experiment_assigned'
  | 'SetLenderName'
  | 'category-d-assigned'
  | 'PrefillAttributeMissing'
  | 'FormlessContinueCtaClicked'

export type GAEventArgs = {
  category: GACategory
  action: GAAction
  label: string
  nonInteraction?: boolean
  lenderName?: string,
  isElegibleForFormlessJourney?: boolean
  prefillData?: boolean
}

export type GADataLayerItem = {
  event: GAAction
  eventModel: {
    event_category: GACategory
    event_label: string
  }
}

export const fireReactGAEvent = (eventValues: GAEventArgs & Record<string, string | boolean>): void => {
  const {
    category, action, label, ...additionalProperties
  } = eventValues
  // This data layer is part of the GA4 requirements.
  try {
    if ($dataLayer) {
      $dataLayer.push({
        event: action,
        eventModel: {
          event_category: category,
          event_label: label,
          ...additionalProperties
        }
      })
    }
  } catch (e) {
    $console.error(`GA4 datalayer event push failed. ${e}. Event: ${action}. Event_category: ${category}. Event_Label: ${label}. Additional Properties: ${JSON.stringify(additionalProperties)}.`)
  }
}

export const trackMultipleInputChoice = ({ label }: Record<string, string>): void => {
  fireReactGAEvent({
    category: 'Application Form',
    action: 'Input',
    label,
    nonInteraction: true
  })
}

type QueryValue = string | (string | null)[] | null

type ReturnDefaultLoanValue = (
  existingAnswers: string,
  loanAmountQuery: {
    loanAmountQuerySnakeCase?: QueryValue,
    loanAmountQueryCamelCase?: QueryValue
  }
) => string

export const numberWithCommas = (num: number) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const returnDefaultLoanAmountValue: ReturnDefaultLoanValue = (
  existingAnswers,
  { loanAmountQuerySnakeCase, loanAmountQueryCamelCase }
) => {
  if (existingAnswers === 'Not sure yet') return ''
  if (Number.parseInt(existingAnswers, 10)) return existingAnswers
  if (loanAmountQuerySnakeCase) return loanAmountQuerySnakeCase as string
  if (loanAmountQueryCamelCase) return loanAmountQueryCamelCase as string
  return ''
}

export const handlePrevButtonClick = (
  dispatcher: React.Dispatch<Action>,
  { name, value, errors, bouncerError }: { name: string; value: unknown; errors?: { [key: string]: boolean }, bouncerError?: string }
): void => {
  dispatcher({
    type: 'SAVE',
    name,
    value,
    errors,
    bouncerError
  })
  dispatcher({
    type: 'PREVIOUS_QUESTION'
  })
}

export const determineRadioVariant = ({
  prominentColourScheme,
  smallRadiosSideBySide,
  sideBySideEnabled
}: {
  prominentColourScheme?: boolean
  smallRadiosSideBySide?: boolean
  sideBySideEnabled?: boolean
}): RadioButtonVariant => {
  if (prominentColourScheme && smallRadiosSideBySide) return 'experimentalDoubleColumnSmallHorizontalProminentColourScheme'
  if (prominentColourScheme) return 'experimentalDoubleColumnSmallProminentColourScheme'
  if (smallRadiosSideBySide) return 'experimental'
  if (sideBySideEnabled) return 'experimentalDoubleColumn'

  return 'experimentalDoubleColumnSmall'
}

export const questionFontStyle: TextStyle | undefined = 'material.body.medium.regular'

export const scrollToTheTop = (): void => {
  window.scrollTo(0, 0)
}

export const getCTAText = (sourceName?: string, isReapply?: boolean) => {
  const sourceToLowerCase = sourceName?.toLowerCase()

  switch (sourceToLowerCase) {
    case 'autotrader':
      return {
        title: 'Get your quote',
        subTextLineOne: '',
        landingTextLine: 'Get a quote with'
      }
    default:
      return isReapply ?
        {
          title: 'Apply now'
        } :
        {
          title: 'Finish',
          subTextLineOne: "See if you'll be accepted",
          landingTextLine: 'See if you\'ll be accepted with'
        }
  }
}

/**
 * Formats a numeric string to a string with comma-separated thousands as per en-GB locale.
 * 
 * @param {string | number} value - The number or numeric string to be formatted.
 * @returns {string} - The formatted string with comma-separated thousands.
 */
export const sanitizeAndFormatGBNumber = (value: string | number): string => {
  const cleanedValue = value.toString().replace(/[^0-9.]/g, '') // Strips out any non-numeric characters except decimal point
  if (!cleanedValue) return ''
  return parseFloat(cleanedValue).toLocaleString('en-GB')
}